import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import laporanService from '../../services/laporan-service';
import dokterService from '../../services/dokter-service';
import pasienService from '../../services/pasien-service';

/* ─── Helpers ─────────────────────────────────────────────── */
const initialFilters = {
  tanggal_mulai: '',
  tanggal_akhir: '',
  dokter_id: '',
  pasien_id: '',
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return date.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const formatResepTindakan = (data) => {
  if (!data) return '-';
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      if (Array.isArray(parsed)) {
        return parsed.map((item, idx) => {
          const lines = [];
          if (item.nama_obat || item.nama) lines.push(`${item.nama_obat || item.nama}`);
          if (item.frekuensi) lines.push(`${item.frekuensi}`);
          if (item.durasi) lines.push(`${item.durasi}`);
          return lines.join(' • ');
        }).join('\n');
      }
      return data;
    } catch {
      return data;
    }
  }
  return data;
};

/* ─── Styles ──────────────────────────────────────────────── */
const thStyle = {
  padding: '12px 16px',
  textAlign: 'left',
  color: '#475569',
  fontWeight: 600,
  textTransform: 'uppercase',
  fontSize: '11px',
  letterSpacing: '0.05em',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '14px 16px',
  verticalAlign: 'top',
  fontSize: '13px',
  color: '#334155',
  lineHeight: '1.5',
};

/* ─── Main Component ──────────────────────────────────────── */
const LaporanRekamMedis = () => {
  const [filters, setFilters] = useState(initialFilters);
  const [laporan, setLaporan] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const fetchOptions = useCallback(async () => {
    try {
      const [dokterRes, pasienRes] = await Promise.all([
        dokterService.getAll(),
        pasienService.getAll(),
      ]);
      setDoctors(Array.isArray(dokterRes) ? dokterRes : dokterRes.data || []);
      setPatients(Array.isArray(pasienRes) ? pasienRes : pasienRes.data || []);
    } catch (err) {
      console.error('Gagal memuat daftar dokter atau pasien', err);
    }
  }, []);

  const fetchLaporan = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = { ...filters };
      Object.keys(params).forEach((key) => {
        if (!params[key]) delete params[key];
      });

      const response = await laporanService.getMedicalRecordReport(params);
      setLaporan(response.data || []);
    } catch (err) {
      console.error(err);
      setError('Gagal mengambil laporan rekam medis. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchOptions();
    fetchLaporan();
  }, [fetchOptions, fetchLaporan]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const resetFilters = () => {
    setFilters(initialFilters);
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExportPdf = async () => {
    setExporting(true);
    setError('');

    try {
      const params = { ...filters };
      Object.keys(params).forEach((key) => {
        if (!params[key]) delete params[key];
      });

      const fileData = await laporanService.exportMedicalRecordPdf(params);
      downloadBlob(fileData, 'laporan-rekam-medis.pdf');
    } catch (err) {
      console.error(err);
      setError('Gagal mengekspor laporan rekam medis ke PDF.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminLayout title="Laporan Rekam Medis">
      <div className="dashboard-section">
        
        {/* Header */}
        <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#0f172a' }}>Filter Rekam Medis</h2>
          <Link to="/admin/alur-kerja" className="view-all" style={{ fontSize: '14px', textDecoration: 'none', color: '#2563eb', fontWeight: 500 }}>
            Kembali ke dashboard laporan →
          </Link>
        </div>

        <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '24px' }}>
          Filter data rekam medis berdasarkan tanggal kunjungan, dokter, atau pasien.
        </p>

        {/* Filter Form */}
        <div className="form" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', backgroundColor: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
          
          <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label htmlFor="tanggal_mulai" style={{ fontSize: '13px', fontWeight: 500, color: '#334155' }}>Tanggal Mulai</label>
            <input
              id="tanggal_mulai"
              name="tanggal_mulai"
              type="date"
              value={filters.tanggal_mulai}
              onChange={handleChange}
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}
            />
          </div>

          <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label htmlFor="tanggal_akhir" style={{ fontSize: '13px', fontWeight: 500, color: '#334155' }}>Tanggal Akhir</label>
            <input
              id="tanggal_akhir"
              name="tanggal_akhir"
              type="date"
              value={filters.tanggal_akhir}
              onChange={handleChange}
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px' }}
            />
          </div>

          <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label htmlFor="dokter_id" style={{ fontSize: '13px', fontWeight: 500, color: '#334155' }}>Dokter</label>
            <select
              id="dokter_id"
              name="dokter_id"
              value={filters.dokter_id}
              onChange={handleChange}
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', backgroundColor: '#fff' }}
            >
              <option value="">Semua Dokter</option>
              {doctors.map((dokter) => (
                <option key={dokter.id} value={dokter.id}>
                  {dokter.nama || dokter.name || 'Dokter'}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <label htmlFor="pasien_id" style={{ fontSize: '13px', fontWeight: 500, color: '#334155' }}>Pasien</label>
            <select
              id="pasien_id"
              name="pasien_id"
              value={filters.pasien_id}
              onChange={handleChange}
              style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', backgroundColor: '#fff' }}
            >
              <option value="">Semua Pasien</option>
              {patients.map((pasien) => (
                <option key={pasien.id} value={pasien.id}>
                  {pasien.nama || pasien.name || 'Pasien'}
                </option>
              ))}
            </select>
          </div>

          {/* Action Buttons */}
          <div className="form-actions" style={{ gridColumn: '1 / -1', display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '8px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
            <button type="button" className="btn" onClick={fetchLaporan} style={{ padding: '10px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#2563eb', color: '#fff', fontSize: '14px', fontWeight: 500, cursor: 'pointer' }}>
              Terapkan Filter
            </button>
            <button type="button" className="btn" onClick={handleExportPdf} disabled={exporting} style={{ padding: '10px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: '#fff', color: '#334155', fontSize: '14px', fontWeight: 500, cursor: 'pointer' }}>
              {exporting ? 'Mengekspor PDF…' : 'Export PDF'}
            </button>
            <button type="button" className="btn danger" onClick={resetFilters} style={{ padding: '10px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#ef4444', color: '#fff', fontSize: '14px', fontWeight: 500, cursor: 'pointer', marginLeft: 'auto' }}>
              Reset Filter
            </button>
          </div>
        </div>

        {error && <p className="form-error" style={{ color: '#ef4444', fontSize: '14px', marginBottom: '16px' }}>{error}</p>}

        {/* Tabel Hasil */}
        <div className="dashboard-section" style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#0f172a', margin: 0 }}>Hasil Laporan Rekam Medis</h3>
            <span style={{ fontSize: '13px', color: '#64748b', backgroundColor: '#f1f5f9', padding: '4px 10px', borderRadius: '12px', fontWeight: 500 }}>
              {laporan.length} Rekam Medis
            </span>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
              <p>Memuat data laporan...</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <table className="table-report" style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ ...thStyle, width: '50px', textAlign: 'center' }}>No.</th>
                    <th style={thStyle}>Pasien & Dokter</th>
                    <th style={thStyle}>Tanggal Kunjungan</th>
                    <th style={thStyle}>Keluhan</th>
                    <th style={thStyle}>Diagnosis</th>
                    <th style={thStyle}>Resep / Tindakan</th>
                  </tr>
                </thead>
                <tbody>
                  {laporan.length > 0 ? (
                    laporan.map((item, index) => (
                      <tr key={item.id || index} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ ...tdStyle, textAlign: 'center', color: '#64748b', fontWeight: 500 }}>
                          {index + 1}
                        </td>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 600, color: '#1e293b' }}>
                            {item.pasien?.nama || item.pasien?.name || '-'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                            Dokter: <span style={{ color: '#475569', fontWeight: 500 }}>{item.dokter?.nama || item.dokter?.name || '-'}</span>
                          </div>
                        </td>
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                          <span style={{ backgroundColor: '#f1f5f9', color: '#334155', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 500 }}>
                            {formatDate(item.tanggal_kunjungan)}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <p style={{ margin: 0, minWidth: '150px' }}>
                            {item.keluhan || item.keluhan_utama || item.keluhan_pasien || '-'}
                          </p>
                        </td>
                        <td style={tdStyle}>
                          <span style={{
                            backgroundColor: '#eff6ff',
                            color: '#1d4ed8',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontWeight: 500,
                            display: 'inline-block'
                          }}>
                            {item.diagnosis || '-'}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          <p style={{ margin: 0, minWidth: '150px', color: '#475569', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {formatResepTindakan(item.tindakan || item.resep || item.treatment) || '-'}
                          </p>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '48px', color: '#94a3b8' }}>
                        <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔍</div>
                        Tidak ada data rekam medis yang sesuai filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default LaporanRekamMedis;